import {
  JobType,
  CreateJobInput,
  SubmitDocumentsForProcessingInput,
  SubmitVerificationInput,
  SubmitGuidelineAnalysisInput,
  FieldUpdate
} from '../graphql/types';
import type { TitleAnalysisInputs } from '../titleAnalysis/inputs';

/**
 * ApiPayloadBuilder - Ensures all API requests include jobType field
 * 
 * This service provides factory functions for building API payloads
 * that are guaranteed to include the jobType field, satisfying
 * Requirements 13.1, 13.3, 13.4, 13.5.
 */

/**
 * Builds the payload for creating a new job.
 * 
 * @param name - The name for the new job
 * @param jobType - The job type (DD or BA)
 * @returns CreateJobInput with jobType included
 * 
 * Requirement 13.3: WHEN creating a job, THE API_Payload SHALL include
 * the jobType from the originating landing page
 */
export function buildCreateJobPayload(
  name: string,
  jobType: JobType
): CreateJobInput {
  return {
    name,
    jobType
  };
}

/**
 * Builds the payload for creating a new Borrower Analysis job with loan data.
 * 
 * @param name - The name for the new job
 * @param jobType - The job type (should be BA)
 * @param loanNumber - The loan number (optional)
 * @param borrowerName - The primary borrower name
 * @param propertyAddress - The property address (optional)
 * @param coBorrowerNames - Array of co-borrower names
 * @returns CreateJobInput with all loan data fields included
 * 
 * Requirements 2.5, 2.6: WHEN the user submits the Loan_Data_Form,
 * THE Frontend SHALL send the loan number, property address, primary
 * borrower name, and all co-borrower names to the Backend as part of
 * the job creation request.
 */
export function buildCreateBAJobPayload(
  name: string,
  jobType: JobType,
  loanNumber: string,
  borrowerName: string,
  propertyAddress: string,
  loanAmount: string,
  coBorrowerNames: string[]
): CreateJobInput {
  return {
    name,
    jobType,
    loanNumber,
    borrowerName,
    propertyAddress,
    loanAmount,
    coBorrowerNames
  };
}

/**
 * Builds the payload for creating a new Title Search (TR) job.
 *
 * Carries every entered title-search field through unchanged and stamps the
 * fixed `TR` job type, mirroring the backend `CreateTitleJobInput` shape.
 *
 * @param name - The name for the new job
 * @param jobType - The job type (should be TR)
 * @param loanNumber - The loan number
 * @param clientName - The client name
 * @param borrowerName - The borrower's name
 * @param effectiveDate - The effective date (ISO 8601 / AWSDate)
 * @param propertyAddress - The property address
 * @param propertyCounty - The property county
 * @param propertyState - The property state
 * @returns CreateJobInput with all title-search fields included
 *
 * Requirements 1.2, 1.3: The Job_Creation_Form sets the job type to
 * "title_report" (TR) and sends all entered fields to the Backend as part of
 * the job creation request.
 */
export function buildCreateTRJobPayload(
  name: string,
  jobType: JobType,
  loanNumber: string,
  clientName: string,
  borrowerName: string,
  effectiveDate: string,
  propertyAddress: string,
  propertyCounty: string,
  propertyState: string,
  titleAnalysisInputs: TitleAnalysisInputs = {}
): CreateJobInput {
  return {
    name,
    jobType,
    loanNumber,
    clientName,
    borrowerName,
    effectiveDate,
    propertyAddress,
    propertyCounty,
    propertyState,
    ...titleAnalysisJobFields(titleAnalysisInputs)
  };
}

function titleAnalysisJobFields(inputs: TitleAnalysisInputs): Partial<CreateJobInput> {
  const text = (value: string | number | null | undefined) =>
    value == null ? '' : String(value).trim();
  const amount = text(inputs.loanAmount);
  const fields: Partial<CreateJobInput> = {
    useCase: text(inputs.useCase),
    vestingEntity: text(inputs.vestingEntity),
    guarantorNames: text(inputs.guarantorNames),
    subjectLoanAmount: amount === '' ? undefined : Number(amount),
    noteDate: text(inputs.noteDate),
    searchStartDate: text(inputs.searchStartDate),
    indexesSearched: inputs.indexesSearched?.length ? [...inputs.indexesSearched] : undefined,
    subjectMortgageRecordingReference: text(inputs.subjectMortgageRecordingReference),
    expectedLienPosition: text(inputs.expectedLienPosition),
    currentLoanHolder: text(inputs.currentLoanHolder),
    apn: text(inputs.apn)
  };
  return Object.fromEntries(
    Object.entries(fields).filter(([, value]) => value !== '' && value !== undefined)
  );
}

/**
 * Builds the payload for submitting documents for processing.
 * 
 * @param jobId - The ID of the job
 * @param tenantId - The tenant ID
 * @param jobType - The job type (DD or BA)
 * @returns SubmitDocumentsForProcessingInput with jobType included
 * 
 * Requirement 13.4: WHEN submitting documents for processing,
 * THE API_Payload SHALL include the jobType of the parent job
 */
export function buildSubmitProcessingPayload(
  jobId: string,
  tenantId: string,
  jobType: JobType
): SubmitDocumentsForProcessingInput {
  return {
    jobId,
    tenantId,
    jobType
  };
}

/**
 * Builds the payload for submitting document verification.
 * 
 * @param jobId - The ID of the job
 * @param tenantId - The tenant ID
 * @param jobType - The job type (DD or BA)
 * @param documentId - The ID of the document being verified
 * @param fieldUpdates - Array of field updates (accept/edit/reject actions)
 * @returns SubmitVerificationInput with jobType included
 * 
 * Requirement 13.1: THE Workflow_System SHALL include a jobType field
 * in all job-related API_Payload requests
 */
export function buildSubmitVerificationPayload(
  jobId: string,
  tenantId: string,
  jobType: JobType,
  documentId: string,
  fieldUpdates: FieldUpdate[]
): SubmitVerificationInput {
  return {
    jobId,
    tenantId,
    jobType,
    documentId,
    fieldUpdates
  };
}

/**
 * Builds the payload for submitting guideline analysis.
 * 
 * @param jobId - The ID of the job
 * @param tenantId - The tenant ID
 * @param jobType - The job type (DD or BA)
 * @param guidelineIds - Array of guideline IDs to analyze against
 * @returns SubmitGuidelineAnalysisInput with jobType included
 * 
 * Requirement 13.5: WHEN requesting guideline analysis,
 * THE API_Payload SHALL include the jobType of the parent job
 */
export function buildSubmitGuidelinePayload(
  jobId: string,
  tenantId: string,
  jobType: JobType,
  guidelineIds: string[]
): SubmitGuidelineAnalysisInput {
  return {
    jobId,
    tenantId,
    jobType,
    guidelineIds
  };
}
